import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

interface LeverageSectionProps {
  badge?: string;
  title?: string;
  description?: string;
  leftImageUrl?: string;
  rightImageUrl?: string;
  leftTitle?: string;
  leftDescription?: string;
  rightTitle?: string;
  rightDescription?: string;
  beforeLabel?: string;
  afterLabel?: string;
}

const DEFAULT_LEFT_IMAGE =
  'https://static.wixstatic.com/media/6ff1e1_1c85ec594a994be88f8f525f2808bd79~mv2.png/v1/fill/w_716,h_402,al_c,lg_1,q_85,enc_avif,quality_auto/6ff1e1_1c85ec594a994be88f8f525f2808bd79~mv2.png';
const DEFAULT_RIGHT_IMAGE =
  'https://static.wixstatic.com/media/6ff1e1_90f6726bec9b4cd3a3ad63791dafd4f2~mv2.jpg/v1/fill/w_388,h_388,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/6ff1e1_90f6726bec9b4cd3a3ad63791dafd4f2~mv2.jpg';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const LeverageSection: React.FC<LeverageSectionProps> = ({
  badge = '喚醒味蕾',
  title = '私廚出品 · 新鮮直送',
  description = '阿力廚房打破傳統網購食材的單調，由主廚把關，將優質食材直送你家。每一份都是精挑細選的用心，每一件都是為喚醒味蕾而生。',
  leftImageUrl = DEFAULT_LEFT_IMAGE,
  rightImageUrl = DEFAULT_RIGHT_IMAGE,
  leftTitle = '私廚匠心製作',
  leftDescription = '由經驗廚師團隊主理，嚴選食材與秘製醬料，每道菜都堅持私廚級出品。',
  rightTitle = '急凍辣餸直送',
  rightDescription = '主廚親自把關，HK$350 起即可安排快遞直送，隨時喚醒你嘅味蕾。',
  beforeLabel = '傳統外賣：單調 · 乏味',
  afterLabel = '阿力廚房：私廚出品 · 喚醒味蕾',
}) => {
  return (
    <section className="py-16 md:py-24 px-4 bg-[#FFFBF5]">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeInUp}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span className="inline-block px-4 py-1.5 bg-[#FFF7ED] text-[#C2410C] text-sm font-bold rounded-full mb-4">
            {badge}
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-4">
            {title}
          </h2>
          <p className="text-stone-600 leading-relaxed">{description}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeInUp}
            className="relative rounded-2xl overflow-hidden group"
          >
            <div className="aspect-[4/3]">
              <img
                src={leftImageUrl}
                alt={leftTitle}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
              <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-xs font-bold rounded-full mb-3">
                匠心秘製
              </span>
              <h3 className="text-xl md:text-2xl font-bold mb-2">{leftTitle}</h3>
              <p className="text-white/90 text-sm md:text-base leading-relaxed">
                {leftDescription}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeInUp}
            className="relative rounded-2xl overflow-hidden group"
          >
            <div className="aspect-[4/3]">
              <img
                src={rightImageUrl}
                alt={rightTitle}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
              <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-xs font-bold rounded-full mb-3">
                新鮮直送
              </span>
              <h3 className="text-xl md:text-2xl font-bold mb-2">{rightTitle}</h3>
              <p className="text-white/90 text-sm md:text-base leading-relaxed">
                {rightDescription}
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeInUp}
          className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6"
        >
          <div className="px-5 py-3 bg-stone-100 text-stone-600 rounded-full text-sm font-medium">
            {beforeLabel}
          </div>
          <div className="text-[#C2410C]">
            <ArrowRight size={20} className="rotate-90 md:rotate-0" />
          </div>
          <div className="px-5 py-3 bg-[#C2410C] text-white rounded-full text-sm font-bold shadow-md">
            {afterLabel}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LeverageSection;
